import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "@phosphor-icons/react";
import { SERVICES } from "@/seoData";
import { QuoteLikeThis } from "@/components/JobStories";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Photo = ({ src, label, alt }) => (
  <div className="relative aspect-[4/3] overflow-hidden rounded-sm bg-[#E4E4E7]">
    <img src={`${BACKEND}${src}?w=640`} srcSet={`${BACKEND}${src}?w=480 480w, ${BACKEND}${src}?w=640 640w, ${BACKEND}${src}?w=960 960w`} sizes="(max-width: 640px) 50vw, 400px" alt={alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
    <span className={`absolute top-3 left-3 text-xs font-display font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-sm text-white ${label === "Before" ? "bg-[#15306E]" : "bg-[#46A01E]"}`}>{label}</span>
  </div>
);

export default function OurWorkSection() {
  const [stories, setStories] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/job-stories`).then(({ data }) => setStories(data)).catch(() => setStories([]));
  }, []);

  return (
    <section id="gallery" className="px-5 lg:px-8 py-20 lg:py-28 bg-[#F4F4F5]" data-testid="our-work-section">
      <div className="max-w-5xl mx-auto">
        <div className="max-w-2xl mb-12">
          <div className="inline-flex items-center gap-2 text-[#46A01E] font-display font-bold uppercase tracking-widest text-xs mb-4">
            <span className="h-px w-8 bg-[#46A01E]" /> Our Work
          </div>
          <h2 className="font-display font-black tracking-tighter text-4xl lg:text-5xl text-[#15306E]">Real jobs. Real results.</h2>
          <p className="text-[#52525B] mt-4 text-lg leading-relaxed">Before-and-after photos from recent junk removal jobs across San Diego County.</p>
        </div>

        {stories && stories.length === 0 && (
          <p className="text-[#52525B]" data-testid="our-work-empty">New before-and-after job photos are on the way.</p>
        )}

        <div className="space-y-10" data-testid="our-work-stories">
          {(stories || []).map((s) => {
            const svc = SERVICES.find((x) => x.slug === s.service_slug);
            const name = svc?.name || "Junk removal";
            return (
              <figure key={s.id} data-testid={`our-work-story-${s.id}`} className="bg-white border border-[#E4E4E7] rounded-sm p-4 lg:p-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Photo src={s.before_url} label="Before" alt={`Before photo, ${name.toLowerCase()} job in ${s.neighborhood}, San Diego`} />
                  <Photo src={s.after_url} label="After" alt={`After photo, ${name.toLowerCase()} job in ${s.neighborhood}, San Diego`} />
                </div>
                <figcaption className="mt-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-display font-extrabold text-lg text-[#15306E]">{name}</p>
                    <p className="flex items-center gap-1.5 text-sm text-[#52525B]"><MapPin size={14} weight="fill" className="text-[#46A01E]" /> {s.neighborhood}</p>
                    {s.caption && <p className="text-sm text-[#52525B] mt-1">{s.caption}</p>}
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    {svc && (
                      <Link to={`/services/${svc.slug}`} data-testid={`our-work-link-${s.id}`} className="inline-flex items-center gap-1.5 text-sm font-display font-bold text-[#46A01E] hover:underline">
                        More about {name.toLowerCase()} <ArrowRight size={14} weight="bold" />
                      </Link>
                    )}
                    <QuoteLikeThis id={s.id} />
                  </div>
                </figcaption>
              </figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}
